import {gql} from '@apollo/client';

import {BillPayerSynchronizationStatus} from 'web-app/api/shared-fragments/integration-sync-status';

export const GetEnabledPaymentSourceTypes = gql`
    query GetEnabledPaymentSourceTypes($institutionSetId: InstitutionSetId!) {
        payments {
            behaviors(institutionSetId: $institutionSetId) {
                behaviorId
                ... on BehaviorHasPossibleSourceTypes {
                    types
                }
            }
        }
    }
`;

const BillPayerWithBalanceFragment = gql`
    fragment BillPayerWithBalance on BillPayerWithBalance {
        balance
        behaviors {
            behaviorId
            __typename
            ... on BehaviorCanMakeAutomaticPayments {
                sourceType
                active
            }
            ... on BehaviorCanSyncExternally {
                status {
                    ...BillPayerSynchronizationStatus
                }
            }
        }
        billPayer {
            accountName
            accountNumber
            billPayerId
            deleted
            institutionId
            email
            sortCode
            phone
            bic
            iban
            name
            billingReference
            note
            childrenThroughShares {
                id
                institutionId
                name {
                    firstName
                    middleName
                    lastName
                    fullName
                }
                profileImage {
                    url
                }
                endDate
                group {
                    id
                    title
                }
            }
        }
        categories {
            id
            name
            siteSetId
        }
        referenceNumber
        automaticInvoicingSchedule
        automaticInvoicingIsEnabled
        availablePaymentsSources {
            type
            active
            status
            title
        }
    }

    ${BillPayerSynchronizationStatus}
`;

export const FetchAvailableBillPayerCategories = gql`
    query FetchAvailableBillPayerCategories($siteSetId: SiteSetId!) {
        finance {
            billPayerCategory {
                forSiteSet(siteSetId: $siteSetId) {
                    id
                    name
                    siteSetId
                }
            }
        }
    }
`;

export const GetBillPayersByInstitutionSetId = gql`
    query GetBillPayerListing(
        $institutionSetIds: [InstitutionSetId!]!
        $next: BillPayerCursor
        $asOfDate: LocalDate
        $createdAfter: LocalDate
        $createdBefore: LocalDate
        $includeDeleted: Boolean
        $onlyMissingAddress: Boolean
        $forPaymentSources: [PaymentsSourceFilterInputType!]
        $includeCreditNotesAndCancelledInvoicesInBalance: Boolean
        $pageSize: Int
        $automaticInvoicingFilter: [AutomaticInvoicingFilterInputType!]
    ) {
        billPayers {
            byInstitutionSetIds(
                institutionSetIds: $institutionSetIds
                next: $next
                asOfDate: $asOfDate
                createdAfter: $createdAfter
                createdBefore: $createdBefore
                includeDeleted: $includeDeleted
                onlyMissingAddress: $onlyMissingAddress
                forPaymentSources: $forPaymentSources
                includeCreditNotesAndCancelledInvoicesInBalance: $includeCreditNotesAndCancelledInvoicesInBalance
                pageSize: $pageSize
                automaticInvoicingFilter: $automaticInvoicingFilter
            ) {
                next
                results {
                    ...BillPayerWithBalance
                }
            }
        }
    }

    ${BillPayerWithBalanceFragment}
`;

export const GetBillPayer = gql`
    query GetBillPayer(
        $billPayerId: BillPayerId!
        $asOfDate: LocalDate
        $includeCreditNotesAndCancelledInvoicesInBalance: Boolean
    ) {
        billPayers {
            byBillPayerIds(
                billPayerIds: [$billPayerId]
                asOfDate: $asOfDate
                includeCreditNotesAndCancelledInvoicesInBalance: $includeCreditNotesAndCancelledInvoicesInBalance
            ) {
                results {
                    ...BillPayerWithBalance
                }
            }
        }
    }

    ${BillPayerWithBalanceFragment}
`;

export const GetActiveXeroConnection = gql`
    query GetActiveXeroConnection($institutionSetId: InstitutionSetId!) {
        finance {
            integration {
                info(institutionSetId: $institutionSetId) {
                    behaviors {
                        __typename
                        behaviorId
                        ... on BehaviorXeroIntegration {
                            tenant {
                                name
                            }
                        }
                    }
                }
            }
        }
    }
`;

export const SynchronizeBillPayers = gql`
    mutation ResyncBillPayers($billPayers: [BillPayerExportInputType!]!) {
        finance {
            integration {
                export {
                    billPayers(billPayers: $billPayers) {
                        details {
                            billPayerId
                            status {
                                ...BillPayerSynchronizationStatus
                            }
                        }
                    }
                }
            }
        }
    }

    ${BillPayerSynchronizationStatus}
`;
